import {
  Grid,
  Tab,
  TabLabel,
  Tabs,
  Typography,
} from '@cambridgeassessment/cambridge-ui';
import { Box } from '@material-ui/core';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import GoBackButton from 'src/components/GoBackButton/GoBackButton';
import Loader from 'src/components/Loader/Loader';
import OrgUserStatus from 'src/components/OrgUserStatus/OrgUserStatus';
import useAuth from 'src/hooks/useAuth';
import { OrgProvider } from 'src/hooks/useOrg';
import { OrgAdminsProvider } from 'src/hooks/useOrgAdmins';
import useOrgAssociateCenters from 'src/hooks/useOrgAssociates';
import { OrgUsersProvider } from 'src/hooks/useOrgUsers';
import {
  OrgAssociateStatus,
  OrgUserStatus as OrgUserStatusType,
  SourceSystem,
} from 'src/types';
import OrgAssociates from './OrgAssociates';
import OrgDetails from './OrgDetails';
import OrgUsers from './OrgUsers';
import { useStyles } from './styles';

export const enum MyOrganisationTabs {
  AssociateSchools = 'Associate Schools',
  Admins = 'Centre Administrators',
  Users = 'Users',
}

const MyOrganisationPage: React.FC = () => {
  const { user, getCurrentOrg } = useAuth();
  const location = useLocation();
  const { associateId } = useParams();
  const history = useHistory();

  const { associateCenters, isCenterParent, updateOrgId, isLoading } =
    useOrgAssociateCenters();

  const pathParts = location.pathname.split('/');
  const isChildPage = useMemo(
    () => pathParts.includes('associate') && pathParts.includes('child'),
    [pathParts]
  );

  const centerIdFromPath = pathParts.find((part) => /^\d+$/.test(part));
  const currentAssociateCenterBPID = associateId || centerIdFromPath || '';

  const currentAssociateCenterData = useMemo(() => {
    if (!currentAssociateCenterBPID || !associateCenters?.length) return null;

    const currentCenter = associateCenters.find(
      (center) => center.bpid === currentAssociateCenterBPID
    );

    if (currentCenter) {
      return {
        bpid: currentCenter.bpid,
        centerNumber: currentCenter.centerNumber,
        centerName: currentCenter.centerName || currentCenter.name,
        name: currentCenter.name,
        status: currentCenter.status,
      };
    }
    return null;
  }, [currentAssociateCenterBPID, associateCenters]);

  const isChildCentre = !isCenterParent || isChildPage;

  const [organizationList, setOrganizationList] = useState<
    Array<{
      id: string;
      name: string;
      legacyNumber?: string;
      childIds?: Array<{
        bpid: string;
        centerNumber: string;
        name: string;
      }>;
    }>
  >([]);

  const [currentOrg, setCurrentOrg] = useState('');

  // This hook pushes the current page to `/organisation` whenever the user changes organisation
  useEffect(() => {
    if (!isChildPage) {
      return;
    }
    if (currentOrg === '') {
      setCurrentOrg(user.organisationLegacyNumber);
      return;
    }
    if (user && user.organisationLegacyNumber !== currentOrg) {
      history.push('/organisation');
      setCurrentOrg(user.organisationLegacyNumber);
    }

    return () => {
      setCurrentOrg('');
    };
  }, [user.organisationName, isChildPage, currentOrg]);

  useEffect(() => {
    if (!user.organisations?.length) return;

    const processedOrgs = user.organisations.map((org) => ({
      id: org.bpid,
      name: org.name,
      legacyNumber: extractLegacyNumber(org.sourceSystems),
      childIds: org.childIds || [],
    }));

    setOrganizationList(processedOrgs);
  }, [user.organisations]);

  const findParentOrganization = useCallback(() => {
    const currentCenterNumber =
      user.organisationLegacyNumber || centerIdFromPath || '50538';

    // If child page, return the currently selected org as the parent org
    if (isChildPage) {
      return {
        name: user.organisationName,
        number: currentCenterNumber,
      };
    }

    const parentOrg = organizationList.find((org) =>
      org.childIds?.some((child) => child.centerNumber === currentCenterNumber)
    );

    if (parentOrg) {
      return {
        name: parentOrg.name,
        number: parentOrg.legacyNumber || '',
      };
    }

    return null;
  }, [
    user.organisationLegacyNumber,
    user.organisationName,
    centerIdFromPath,
    isChildPage,
    organizationList,
  ]);

  const parentOrganization = useMemo(() => {
    const parent = findParentOrganization();
    return parent;
  }, [findParentOrganization, isChildPage, isChildCentre]);

  if (!isChildPage) updateOrgId(user.organisationId);

  const getInitialTabIndex = () => {
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');

    if (tabParam === 'users') {
      return 1;
    }
    const isReturned = history.action === 'POP';
    let index: number;
    if (isReturned) {
      if (associateId) {
        index = Number(sessionStorage.getItem('childIndex'));
      } else {
        index = Number(sessionStorage.getItem('parentIndex'));
      }
      return index;
    } else {
      sessionStorage.setItem('parentIndex', '0');
      sessionStorage.setItem('childIndex', '0');
    }
    return 0;
  };

  const extractLegacyNumber = (
    sourceSystems: (SourceSystem | string)[]
  ): string => {
    if (!sourceSystems || !sourceSystems.length) return '';

    const orgId =
      sourceSystems?.find(
        (source): source is SourceSystem =>
          (source as SourceSystem).systemId === 'LegacyCustomerNum'
      )?.orgId || false;

    if (orgId) return orgId;

    const legacySource = (sourceSystems as string[])[0].match(/=(.*)$/);

    return legacySource ? legacySource[1] : '';
  };

  const classes = useStyles();
  const [tabIndex, setTabIndex] = useState(getInitialTabIndex);

  const orgName =
    isChildPage && currentAssociateCenterData
      ? `${currentAssociateCenterData.centerNumber} - ${currentAssociateCenterData.centerName}`
      : user.organisationLegacyNumber
      ? `${user.organisationLegacyNumber} - ${user.organisationName}`
      : user.organisationName;

  let content: JSX.Element;

  if (isChildPage) {
    content =
      tabIndex === 0 ? (
        <OrgProvider>
          <OrgAdminsProvider orgId={associateId}>
            <OrgDetails orgId={associateId} />
          </OrgAdminsProvider>
        </OrgProvider>
      ) : (
        <OrgProvider>
          <OrgUsersProvider orgId={associateId}>
            <OrgUsers orgId={associateId} />
          </OrgUsersProvider>
        </OrgProvider>
      );
  } else {
    if (
      associateCenters.length !== 0 && // should only display OrgAssociates if there are associate centers
      isCenterParent &&
      (getCurrentOrg()?.centerType || '').toLowerCase() !== 'standard'
    ) {
      content =
        tabIndex === 0 ? (
          <OrgProvider>
            <OrgAssociates />
          </OrgProvider>
        ) : tabIndex === 1 ? (
          <OrgProvider>
            <OrgAdminsProvider orgId={associateId}>
              <OrgDetails orgId={user.organisationId} />
            </OrgAdminsProvider>
          </OrgProvider>
        ) : (
          <OrgProvider>
            <OrgUsersProvider orgId={user.organisationId}>
              <OrgUsers orgId={user.organisationId} />
            </OrgUsersProvider>
          </OrgProvider>
        );
    } else {
      content =
        tabIndex === 0 ? (
          <OrgProvider>
            <OrgAdminsProvider orgId={user.organisationId}>
              <OrgDetails orgId={user.organisationId} />
            </OrgAdminsProvider>
          </OrgProvider>
        ) : (
          <OrgProvider>
            <OrgUsersProvider orgId={user.organisationId}>
              <OrgUsers orgId={user.organisationId} />
            </OrgUsersProvider>
          </OrgProvider>
        );
    }
  }

  useEffect(() => {
    const index = getInitialTabIndex();
    setTabIndex(index);
  }, [isChildPage]);

  const convertAssociateStatusToUserStatus = (
    status?: OrgAssociateStatus
  ): OrgUserStatusType => {
    if (!status) return OrgUserStatusType.Active;

    switch (status) {
      case OrgAssociateStatus.Active:
        return OrgUserStatusType.Active;
      case OrgAssociateStatus.Inactive:
        return OrgUserStatusType.Inactive;
      default:
        return OrgUserStatusType.Active;
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box>
      {isChildPage && <GoBackButton />}

      <Typography variant="h2" className={classes.title} data-testid="title">
        {orgName}
        {(isCenterParent || isChildCentre) && (
          <OrgUserStatus
            status={
              isChildCentre
                ? convertAssociateStatusToUserStatus(
                    currentAssociateCenterData?.status
                  )
                : OrgUserStatusType.Active
            }
          />
        )}
      </Typography>
      <Box className={classes.gridWrapper}>
        <Grid container spacing={1}>
          <Grid
            container
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            spacing={3}
          >
            {(getCurrentOrg()?.centerType || '').toLowerCase() ===
            'standard' ? null : (
              <>
                <Grid item xs={4}>
                  <Box mb={1}>
                    <Typography variant="subtitle1">Centre type:</Typography>
                  </Box>
                  <Typography variant="body1">
                    {isChildCentre ? 'Associate School' : 'Associate'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Box mb={1}>
                    <Typography variant="subtitle1">
                      {isChildCentre
                        ? 'Associate School number:'
                        : 'Centre number:'}
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {isChildCentre
                      ? currentAssociateCenterData?.centerNumber ||
                        user.organisationLegacyNumber
                      : user.organisationLegacyNumber}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>

          {isChildCentre &&
            parentOrganization &&
            (getCurrentOrg()?.centerType || '').toLowerCase() !==
              'standard' && (
              <Grid
                container
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                xl={12}
                spacing={3}
              >
                <Grid item xs={4}>
                  <Box mb={1}>
                    <Typography variant="subtitle1">Associate name:</Typography>
                  </Box>
                  <Typography variant="body1">
                    {parentOrganization?.name}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Box mb={1}>
                    <Typography variant="subtitle1">Centre number:</Typography>
                  </Box>
                  <Typography variant="body1">
                    {parentOrganization?.number}
                  </Typography>
                </Grid>
              </Grid>
            )}
        </Grid>
      </Box>

      <Tabs
        onChange={
          ((_, value) => {
            setTabIndex(value);
            if (associateId) {
              sessionStorage.setItem('childIndex', value);
            } else {
              sessionStorage.setItem('parentIndex', value);
            }
          }) as any
        }
        value={tabIndex}
        className={classes.tabs}
        data-testid="tabs"
      >
        {associateCenters.length !== 0 && // should only display associate school tab if there are associate centers
          !isChildPage &&
          isCenterParent &&
          (getCurrentOrg()?.centerType || '').toLowerCase() !== 'standard' && (
            <Tab
              label={<TabLabel label={MyOrganisationTabs.AssociateSchools} />}
              data-testid="associate-schools-tab"
            />
          )}

        <Tab
          label={<TabLabel label={MyOrganisationTabs.Admins} />}
          data-testid="admins-tab"
        />
        <Tab
          label={<TabLabel label={MyOrganisationTabs.Users} />}
          data-testid="users-tab"
        />
      </Tabs>
      {content}
    </Box>
  );
};

export default memo(MyOrganisationPage);
